import React, { useMemo, useState } from 'react';
import type { PcV2Bundle, PcV2Channel, PcV2Entry } from '../../patient-comms/typesV2';
import {
  PC_V2_CHANNELS,
  PC_V2_CHANNEL_PLURAL,
  pcV2CountsByChannel,
  pcV2FormatWhen,
  pcV2Label,
  pcV2SortEntries,
} from '../../patient-comms/utilsV2';
import { Icon } from '../Icon/Icon';
import { PatientCommsChannelTiles } from './comms-v2/PatientCommsChannelTiles';

export interface PatientCommsTabContentV2Props {
  bundle: PcV2Bundle;
}

function CommsRow({ entry }: { entry: PcV2Entry }) {
  const ch = PC_V2_CHANNELS[entry.channel];
  return (
    <div className="flex gap-3 border-b border-[var(--cream-2)] py-[13px] last:border-b-0">
      <span
        className="mt-[2px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md"
        style={{ background: `color-mix(in srgb, ${ch.color} 12%, var(--cream-1))`, color: ch.color }}
      >
        <Icon kind={ch.icon} size={11} stroke={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span
            className="font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.08em]"
            style={{ color: ch.color }}
          >
            {pcV2Label(entry)}
          </span>
          <span className="shrink-0 text-[11.5px] text-[var(--ink-4)]">{pcV2FormatWhen(entry.at)}</span>
        </div>
        <p className="m-0 mt-[3px] text-[13px] leading-[1.55] text-[var(--ink-1)]">{entry.summary}</p>
      </div>
    </div>
  );
}

/**
 * Communications, v2 — one read-only history, newest first.
 *
 * Everything that framed the v1 tab is gone: the stat tiles, the AI summary,
 * the channel filter, the week/month buckets, the status chips, the expandable
 * detail panes and their action buttons. What the front desk asks of this tab
 * is "what have we said to this person, and when" — a single list answers that,
 * and every box around it was a second thing to read first.
 *
 * Three channels only. Recalls and reminders are reasons, not channels, so they
 * appear as the text or email they were sent as. Inbound email is excluded: it
 * arrives from the practice's own Gmail/Outlook sync and belongs to the Email
 * module's inbox. Surveys are absent because there is no Survey module.
 */
export function PatientCommsTabContentV2({ bundle }: PatientCommsTabContentV2Props) {
  const [channel, setChannel] = useState<PcV2Channel | null>(null);

  const all = useMemo(() => pcV2SortEntries(bundle.entries), [bundle.entries]);
  const counts = useMemo(() => pcV2CountsByChannel(all), [all]);
  const entries = useMemo(
    () => (channel === null ? all : all.filter((e) => e.channel === channel)),
    [all, channel],
  );

  return (
    <div className="pc-rise mx-auto w-full max-w-[820px]">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="m-0 font-[var(--font-display)] text-[19px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Communications
        </h2>
        <span className="text-[12px] text-[var(--ink-4)]">
          {entries.length === 1 ? '1 message' : `${String(entries.length)} messages`}
          {channel === null ? '' : ` · ${PC_V2_CHANNEL_PLURAL[channel].toLowerCase()} only`}
        </span>
      </div>

      <PatientCommsChannelTiles counts={counts} active={channel} onChange={setChannel} />

      {entries.length === 0 ? (
        <div className="py-[60px] text-center text-[13.5px] text-[var(--ink-3)]">
          {channel === null
            ? 'No texts, calls or emails with this patient yet.'
            : `No ${PC_V2_CHANNEL_PLURAL[channel].toLowerCase()} with this patient yet.`}
        </div>
      ) : (
        <div className="flex flex-col">
          {entries.map((entry) => (
            <CommsRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      <div className="h-16 shrink-0" aria-hidden />
    </div>
  );
}
